import { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from '@tanstack/react-router';
import logoLight from '@/assets/images/Logo-light.png';
import logoDark from '@/assets/images/Logo-dark.png';


const InitialLoader = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (!loading && !hasNavigated.current) {
      hasNavigated.current = true;
      if (isAuthenticated && user) {
        navigate({ to: '/dashboard' });
      } else {
        navigate({ to: '/login' });
      }
    }
  }, [loading, isAuthenticated, user, navigate]);

  return (
    <div
      className="min-h-screen w-screen flex flex-col items-center justify-center relative overflow-hidden font-outfit"
      style={{ background: 'var(--loader-bg)' }}
    >
      <style>{`
        :root {
          --loader-bg: #fafafa;
          --loader-line: #e5e5e5;
          --loader-dot: #a3a3a3;
          --loader-text: #737373;
          --loader-accent: #171717;
          --loader-glow: rgba(0,0,0,0.04);
          --loader-shine: rgba(255,255,255,0.0);
        }
        .dark {
          --loader-bg: #09090b;
          --loader-line: #27272a;
          --loader-dot: #52525b;
          --loader-text: #71717a;
          --loader-accent: #fafafa;
          --loader-glow: rgba(255,255,255,0.03);
          --loader-shine: rgba(255,255,255,0.02);
        }

        @keyframes loaderFadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes loaderFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes loaderSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes loaderPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.95); }
          50%       { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes loaderSlide {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes loaderOrb {
          0%, 100% { transform: translate(0px, 0px); }
          33%       { transform: translate(30px, -20px); }
          66%       { transform: translate(-20px, 15px); }
        }
        @keyframes loaderOrbB {
          0%, 100% { transform: translate(0px, 0px); }
          33%       { transform: translate(-25px, 20px); }
          66%       { transform: translate(20px, -25px); }
        }

        .loader-wrap {
          animation: loaderFadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
        .loader-sub {
          animation: loaderFadeIn 0.6s ease forwards 0.5s;
          opacity: 0;
        }
        .loader-bar-wrap {
          animation: loaderFadeIn 0.6s ease forwards 0.7s;
          opacity: 0;
        }
        .loader-footer {
          animation: loaderFadeIn 0.6s ease forwards 1s;
          opacity: 0;
        }
      `}</style>

      {/* ── Ambient orbs — very subtle ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* light: barely visible warm/cool tints | dark: deeper glow */}
        <div
          className="absolute rounded-full"
          style={{
            width: 600, height: 600,
            top: '-15%', left: '-10%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 65%)',
            filter: 'blur(40px)',
            animation: 'loaderOrb 20s ease-in-out infinite',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 500, height: 500,
            bottom: '-10%', right: '-8%',
            background: 'radial-gradient(circle, rgba(14,165,233,0.06) 0%, transparent 65%)',
            filter: 'blur(40px)',
            animation: 'loaderOrbB 24s ease-in-out infinite',
          }}
        />
      </div>

      {/* ── Fine grid ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(var(--loader-line) 1px, transparent 1px),
            linear-gradient(90deg, var(--loader-line) 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px',
          opacity: 0.35,
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
        }}
      />

      {/* ── Center content ── */}
      <div className="loader-wrap relative z-10 flex flex-col items-center gap-10">

        {/* Logo */}
        <div className="relative flex items-center justify-center">
          {/* Soft glow ring behind logo */}
          <div
            className="absolute rounded-full"
            style={{
              width: 180, height: 180,
              background: 'radial-gradient(circle, var(--loader-glow) 0%, transparent 70%)',
              animation: 'loaderPulse 4s ease-in-out infinite',
            }}
          />
          <img
            src={logoLight}
            className="block dark:hidden relative z-10 h-12 w-auto"
            alt="Logo"
            fetchPriority="high"
          />
          <img
            src={logoDark}
            className="hidden dark:block relative z-10 h-12 w-auto"
            alt="Logo"
            fetchPriority="high"
          />
        </div>

        {/* Spinner + label */}
        <div className="loader-sub flex flex-col items-center gap-4">
          {/* Elegant thin ring spinner */}
          <div className="relative w-8 h-8">
            {/* Track */}
            <div
              className="absolute inset-0 rounded-full"
              style={{ border: '1.5px solid var(--loader-line)' }}
            />
            {/* Spinning arc */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                border: '1.5px solid transparent',
                borderTopColor: 'var(--loader-dot)',
                borderRightColor: 'var(--loader-dot)',
                animation: 'loaderSpin 0.9s linear infinite',
              }}
            />
            {/* Center dot */}
            <div
              className="absolute inset-0 m-auto w-1 h-1 rounded-full"
              style={{ background: 'var(--loader-dot)' }}
            />
          </div>

          <p
            className="text-[11px] tracking-[0.25em] uppercase"
            style={{ color: 'var(--loader-text)' }}
          >
            Cargando
          </p>
        </div>

        <div className="loader-bar-wrap flex flex-col items-center gap-3">
          <div
            className="rounded-full overflow-hidden"
            style={{
              width: 160,
              height: 1,
              background: 'var(--loader-line)',
            }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: '35%',
                background: 'var(--loader-dot)',
                animation: 'loaderSlide 1.6s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </div>


      <div
        className="loader-footer absolute bottom-8 flex items-center gap-3"
        style={{ color: 'var(--loader-dot)' }}>
        <div style={{ width: 16, height: 1, background: 'var(--loader-line)' }} />
        <span className="text-[10px] tracking-[0.35em] uppercase font-light">v 1.0.0</span>
        <div style={{ width: 16, height: 1, background: 'var(--loader-line)' }} />
      </div>
    </div>
  );
};

export default InitialLoader;